import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  AlertCircle,
  ArrowLeft,
  LayoutGrid,
  Loader2,
  RotateCcw,
  Save,
  ShieldCheck,
} from 'lucide-react';
import PageBreadcrumb from '@/shared/PageBreadCrumb';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';
import type { Role } from '@/types/role';
import { useMenuConfig } from '../hooks/useMenuConfig';
import MenuItemCard from '../components/menu-config/MenuItemCard';
import SidebarPreview from '../components/menu-config/SidebarPreview';
import MenuLibraryPanel from '../components/menu-config/MenuLibraryPanel';

/**
 * Dedicated Menu Configuration page — customise the sidebar for one role.
 * Route: /admin/roles/:roleId/menu (role optionally passed via location.state
 * from the Roles table; the page is deep-link safe without it).
 */
export default function RoleMenuConfig() {
  const { roleId = '' } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const stateRole = (location.state as { role?: Role } | null)?.role;

  const api = useMenuConfig(roleId);
  const roleName = api.roleName || stateRole?.name || '';
  const department = stateRole?.department;
  // Default (system) roles are locked — the whole page is read-only; the lock
  // can only be lifted by changing isDefault directly in the database.
  const isLocked = api.isDefaultRole || !!stateRole?.isDefault;

  // Warn on hard navigation (reload / close) while there are unsaved changes.
  useEffect(() => {
    if (!api.isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [api.isDirty]);

  const goBack = useCallback(() => {
    if (
      api.isDirty &&
      !window.confirm('You have unsaved menu changes. Leave without saving?')
    ) {
      return;
    }
    navigate('/admin/roles');
  }, [api.isDirty, navigate]);

  // PMO command-center ledger style — typographic columns, no boxes.
  const kpis = [
    { label: 'In sidebar', value: api.counts.total, caption: 'items configured', ink: '#0B3B2E' },
    { label: 'Visible', value: api.counts.visible, caption: 'shown to this role', ink: '#1F5A47' },
    { label: 'Hidden', value: api.counts.hidden, caption: 'kept but not shown', ink: '#9F1239' },
    { label: 'Disabled', value: api.counts.disabled, caption: 'entries inactive', ink: '#5A6275' },
  ];

  return (
    <div className="mx-auto max-w-9xl space-y-5 pb-24">
      <PageBreadcrumb
        pageTitle="Menu Configuration"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Role Management', path: '/admin/roles' },
          { name: 'Menu Configuration' },
        ]}
      />

      {/* ── Hero header ──────────────────────────────────────────────────── */}
      <div
        className="relative rounded-2xl px-5 py-4"
        style={{
          background: SOVEREIGN.cardBg,
          border: `1px solid ${SOVEREIGN.cardBorder}`,
          boxShadow: SOVEREIGN.cardShadow,
        }}
      >
        {/* Engraved banknote inner rule */}
        <span
          aria-hidden
          className="pointer-events-none absolute"
          style={{
            inset: 7,
            border: '1px dashed rgba(11,59,46,0.18)',
            borderRadius: 11,
          }}
        />
        <div className="relative flex flex-wrap items-center gap-x-6 gap-y-4">
          {/* Title block */}
          <div className="flex min-w-0 items-center gap-4 lg:w-[300px] lg:shrink-0">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
              style={{
                background: 'rgba(201,169,97,0.16)',
                border: '1px solid rgba(201,169,97,0.40)',
              }}
            >
              <LayoutGrid size={22} style={{ color: '#8A5A08' }} />
            </div>
            <div className="min-w-0">
              <h1
                className="text-xl font-extrabold tracking-tight"
                style={{ color: SOVEREIGN.title }}
              >
                Menu Configuration
              </h1>
              <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[12.5px]" style={{ color: SOVEREIGN.kpiCaption }}>
                Customize the sidebar for
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11.5px] font-bold"
                  style={{
                    background: 'rgba(31,90,71,0.08)',
                    border: '1px solid rgba(31,90,71,0.20)',
                    color: SOVEREIGN.bottle,
                  }}
                >
                  <ShieldCheck size={12} />
                  {roleName || '…'}
                </span>
                {department && (
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                    style={{
                      background: 'rgba(201,169,97,0.14)',
                      border: '1px solid rgba(201,169,97,0.35)',
                      color: '#8A5A08',
                    }}
                  >
                    {department}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="hidden w-px self-stretch bg-[rgba(11,59,46,0.12)] lg:block" />

          {/* KPI ledger — PMO command-center style: typographic columns, no boxes */}
          <div className="flex min-w-0 flex-1 flex-wrap content-center gap-y-4">
            {kpis.map((item, i) => (
              <div
                key={item.label}
                className={`min-w-[96px] flex-1 ${i > 0 ? 'border-l border-[rgba(11,59,46,0.1)] pl-5' : ''}`}
              >
                <p className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.14em] text-[#7C8A82]">
                  {item.label}
                </p>
                <p
                  className="mt-1 text-[26px] font-extrabold leading-none tabular-nums tracking-tight"
                  style={{ color: item.ink }}
                >
                  {api.isLoading ? '—' : item.value}
                </p>
                <p className="mt-1 whitespace-nowrap text-[10px] font-medium text-[#7C8A82]">
                  {item.caption}
                </p>
              </div>
            ))}
          </div>

          <div className="hidden w-px self-stretch bg-[rgba(11,59,46,0.12)] lg:block" />

          <button
            type="button"
            onClick={goBack}
            className="relative flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12.5px] font-semibold transition-colors"
            style={{
              background: SOVEREIGN.inputBg,
              border: `1px solid ${SOVEREIGN.inputBorder}`,
              color: SOVEREIGN.title,
            }}
          >
            <ArrowLeft size={14} />
            Back to roles
          </button>
        </div>
      </div>

      {/* ── Locked banner (default roles) ────────────────────────────────── */}
      {isLocked && !api.isLoading && (
        <div
          className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm"
          style={{
            background: 'rgba(201,169,97,0.10)',
            border: '1px solid rgba(201,169,97,0.40)',
            color: '#8A5A08',
          }}
          role="alert"
        >
          <ShieldCheck size={15} className="shrink-0" />
          <span>
            <strong>{roleName || 'This role'}</strong> is a system default role — its menu
            configuration is locked and cannot be changed from the application.
          </span>
        </div>
      )}

      {/* ── Error banner ─────────────────────────────────────────────────── */}
      {api.error && (
        <div
          className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm"
          style={{
            background: 'rgba(159,18,57,0.06)',
            border: '1px solid rgba(159,18,57,0.25)',
            color: '#9F1239',
          }}
          role="alert"
        >
          <AlertCircle size={15} className="shrink-0" />
          {api.error}
        </div>
      )}

      {api.isLoading ? (
        <div className="flex flex-col items-center justify-center py-28">
          <Loader2 className="mb-3 h-9 w-9 animate-spin" style={{ color: SOVEREIGN.brand }} />
          <p className="text-sm font-medium" style={{ color: SOVEREIGN.kpiCaption }}>
            Loading menu configuration…
          </p>
        </div>
      ) : (
        <DndProvider backend={HTML5Backend}>
          {/* ── Content grid ──────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            {/* Left — sidebar items */}
            <div className="min-w-0 space-y-3">
              <div className="flex items-center justify-between px-1">
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: SOVEREIGN.ink }}
                >
                  Sidebar items · drag to reorder
                </p>
                <p className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                  Power = enable · Eye = show/hide (order is kept)
                </p>
              </div>

              {api.menuItems.length === 0 ? (
                <div
                  className="rounded-2xl px-6 py-16 text-center"
                  style={{
                    background: SOVEREIGN.cardBg,
                    border: `1px dashed ${SOVEREIGN.inputBorder}`,
                  }}
                >
                  <div
                    className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full"
                    style={{ background: 'rgba(201,169,97,0.14)' }}
                  >
                    <LayoutGrid className="h-7 w-7" style={{ color: SOVEREIGN.champagne }} />
                  </div>
                  <h3 className="mb-1 text-base font-bold" style={{ color: SOVEREIGN.title }}>
                    No menu items configured
                  </h3>
                  <p className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
                    Add features from the Menu library to build this role's sidebar.
                  </p>
                </div>
              ) : (
                api.menuItems.map((item, index) => (
                  <MenuItemCard
                    key={item.id}
                    item={item}
                    index={index}
                    totalItems={api.menuItems.length}
                    api={api}
                  />
                ))
              )}
            </div>

            {/* Right — sticky preview + library */}
            <div className="space-y-4 lg:sticky lg:top-4">
              <SidebarPreview api={api} roleName={roleName} />
              <MenuLibraryPanel api={api} />
            </div>
          </div>
        </DndProvider>
      )}

      {/* ── Sticky action bar ────────────────────────────────────────────── */}
      {!api.isLoading && (
        <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4">
          <div
            className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 rounded-2xl px-5 py-3"
            style={{
              background: SOVEREIGN.cardBg,
              border: `1px solid ${SOVEREIGN.cardBorder}`,
              boxShadow: '0 18px 44px -12px rgba(42,31,23,0.45)',
            }}
          >
            <div className="flex items-center gap-3">
              {api.isDirty ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold"
                  style={{
                    background: 'rgba(201,169,97,0.16)',
                    border: '1px solid rgba(201,169,97,0.40)',
                    color: '#8A5A08',
                  }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: SOVEREIGN.champagne }}
                  />
                  Unsaved changes
                </span>
              ) : (
                <span className="text-[11.5px] font-medium" style={{ color: SOVEREIGN.kpiCaption }}>
                  All changes saved
                </span>
              )}
              <span className="hidden text-[11.5px] sm:block" style={{ color: SOVEREIGN.kpiCaption }}>
                Sidebar display only — permissions are not affected.
              </span>
            </div>

            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={api.discard}
                disabled={!api.isDirty || api.isSaving}
                className="flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45"
                style={{
                  background: SOVEREIGN.inputBg,
                  border: `1px solid ${SOVEREIGN.inputBorder}`,
                  color: SOVEREIGN.title,
                }}
              >
                <RotateCcw size={13} />
                Discard
              </button>
              <button
                type="button"
                onClick={() => void api.save()}
                disabled={isLocked || !api.isDirty || api.isSaving || api.menuItems.length === 0}
                title={isLocked ? 'Menu configuration is locked for default roles' : undefined}
                className="flex min-w-[150px] items-center justify-center gap-2 rounded-lg px-5 py-2 text-[12.5px] font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-55"
                style={{
                  background: SOVEREIGN.btn,
                  border: `1px solid ${SOVEREIGN.btnBorder}`,
                  boxShadow: SOVEREIGN.btnShadow,
                }}
              >
                {api.isSaving ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <Save size={14} />
                    Save configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
